import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Search, User, Image, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const QCListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const [qcEntries, setQcEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoQC, setPhotoQC] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  useEffect(() => {
    fetchQCEntries();
  }, [startDate, endDate]);

  useEffect(() => {
    const filtered = qcEntries.filter(
      (qc) =>
        (qc.asset_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (qc.customer_or_company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (qc.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEntries(filtered);
  }, [searchTerm, qcEntries]);

  const fetchQCEntries = async () => {
    try {
      const response = await api.get('/qc');
      let data = response.data;

      // Filter by date range if provided via URL params
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        data = data.filter((qc) => {
          const qcDate = new Date(qc.created_timestamp);
          return qcDate >= start && qcDate <= end;
        });
      }

      setQcEntries(data);
      setFilteredEntries(data);
    } catch (error) {
      console.error('Failed to fetch QC entries:', error);
      toast.error('Failed to load QC entries');
    } finally {
      setLoading(false);
    }
  };

  // Fetch photos on demand when user clicks View Photos
  const handleViewPhotos = async (qc) => {
    setPhotoLoading(true);
    setShowPhotoDialog(true);
    try {
      const response = await api.get(`/qc/${qc.id}`);
      setPhotoQC(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      toast.error('Failed to load photos');
      setPhotoQC(qc);
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleDeleteQC = async (qcId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/qc/${qcId}`);
      toast.success('QC entry deleted successfully');
      setQcEntries((prev) => prev.filter((qc) => qc.id !== qcId));
      setFilteredEntries((prev) => prev.filter((qc) => qc.id !== qcId));
    } catch (error) {
      console.error('Failed to delete QC entry:', error);
      toast.error('Failed to delete QC entry');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="qc-list-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">My QC Entries</h1>
        <p className="text-lg text-muted-foreground">
          View and manage your QC checks
          {startDate && endDate && (
            <span className="ml-2 text-sm font-medium">
              ({new Date(startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              {startDate !== endDate && ` - ${new Date(endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`})
            </span>
          )}
        </p>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            data-testid="search-qc-input"
            placeholder="Search by Asset ID, Customer, or Serial Number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-14 text-lg"
          />
        </div>
      </div>

      {filteredEntries.length === 0 ? (
        <p className="text-center py-12 text-muted-foreground">No QC entries found</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredEntries.map((qc) => (
            <div
              key={qc.id}
              data-testid={`qc-card-${qc.id}`}
              onClick={() => navigate(`/qc/${qc.id}`)}
              className="bg-white rounded-xl border border-border shadow-sm p-6 relative overflow-hidden hover:shadow-md transition-shadow cursor-pointer active:scale-[0.98] transition-transform duration-200"
            >
              <div className="absolute top-4 right-4">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                    qc.qc_status === 'PASS'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : qc.qc_status === 'HOLD'
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}
                >
                  {qc.qc_status}
                </span>
              </div>

              <div className="pr-24">
                <h3 className="text-2xl font-bold mb-2">{qc.asset_id}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p>
                    <strong>Customer:</strong> {qc.customer_or_company_name}
                  </p>
                  <p>
                    <strong>Product:</strong> {qc.product_category}
                  </p>
                  <p>
                    <strong>Event:</strong> {qc.qc_event_type}
                  </p>
                  <p>
                    <strong>Score:</strong> {qc.qc_score}/100
                  </p>
                  <p>
                    <strong>Created:</strong> {new Date(qc.created_timestamp).toLocaleString()}
                  </p>
                  <p>
                    <strong>Second Check:</strong> {qc.second_check_status}
                  </p>
                </div>
                {qc.second_check_technician && (
                  <div className="mt-3">
                    <span className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                      <User className="w-4 h-4 mr-2" />
                      Assigned to: {qc.second_check_technician}
                    </span>
                  </div>
                )}
                {qc.hold_reasons && qc.hold_reasons.length > 0 && (
                  <div className="mt-3">
                    <p className="font-bold text-destructive text-sm mb-1">Hold Reasons:</p>
                    <div className="flex flex-wrap gap-2">
                      {qc.hold_reasons.map((reason, idx) => (
                        <Badge key={idx} variant="destructive">
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewPhotos(qc);
                    }}
                    data-testid={`view-photos-${qc.id}`}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    View Photos
                  </Button>
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`delete-qc-${qc.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure you want to delete this entry?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove QC entry <strong>{qc.asset_id}</strong> ({qc.customer_or_company_name}). This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={(e) => handleDeleteQC(qc.id, e)}
                            data-testid={`confirm-delete-${qc.id}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Viewing Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>QC Photos - {photoQC?.asset_id}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {photoQC?.photos?.length || 0} photo(s)
              </span>
            </DialogTitle>
          </DialogHeader>
          {photoLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading photos...</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {photoQC?.photos?.map((photo, idx) => (
                  <div key={idx} className="relative border rounded-lg overflow-hidden">
                    <img
                      src={photo.data}
                      alt={`QC Photo ${idx + 1}`}
                      className="w-full h-auto object-contain max-h-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2">
                      <p><strong>Taken:</strong> {photo.formatted_timestamp || photo.timestamp || 'N/A'}</p>
                      <p><strong>Category:</strong> {photo.category || 'General'}</p>
                    </div>
                  </div>
                ))}
              </div>
              {(!photoQC?.photos || photoQC.photos.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No photos available for this QC entry</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QCListPage;